import { ArrowLeft, Home, Mail, Monitor, UserCircle, Workflow } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../auth/useAuth";
import ProfileSettingsPanel from "../settings/ProfileSettingsPanel";
import { getProfileExtras } from "../settings/profileSettingsService";
import devicesLogo from "../../assets/logos/Oikos_Student_Devices_Logo.png";

function getInitials(name = "", email = "") {
  const source = String(name || email || "U").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return source.slice(0, 2).toUpperCase() || "U";
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function getMetadataObject(value = null) {
  return value && typeof value === "object" ? value : {};
}

function getProfileImageUrl({ profile = null, user = null, extras = null } = {}) {
  const profileMetadata = getMetadataObject(profile?.metadata);
  const profileExtraData = getMetadataObject(profileMetadata.extra_data);
  const userMetadata = getMetadataObject(user?.user_metadata);
  const userExtraData = getMetadataObject(userMetadata.extra_data);
  const identityData = getMetadataObject(user?.identities?.[0]?.identity_data);

  return (
    extras?.avatarUrl ||
    profileMetadata.avatar_url ||
    profileMetadata.avatarUrl ||
    profileMetadata.picture ||
    profileMetadata.profile_image_url ||
    profileMetadata.profileImageUrl ||
    profileExtraData.avatar_url ||
    profileExtraData.avatarUrl ||
    profileExtraData.picture ||
    profileExtraData.profile_image_url ||
    profileExtraData.profileImageUrl ||
    userMetadata.avatar_url ||
    userMetadata.avatarUrl ||
    userMetadata.picture ||
    userMetadata.profile_image_url ||
    userMetadata.profileImageUrl ||
    userExtraData.avatar_url ||
    userExtraData.avatarUrl ||
    userExtraData.picture ||
    userExtraData.profile_image_url ||
    userExtraData.profileImageUrl ||
    identityData.avatar_url ||
    identityData.avatarUrl ||
    identityData.picture ||
    ""
  );
}

export default function UserProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile } = useAuth();
  const extras = useMemo(() => getProfileExtras(profile), [profile]);
  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Your Profile";
  const displayEmail = profile?.email || user?.email || "";
  const profileImageUrl = getProfileImageUrl({ profile, user, extras });
  const returnPath = searchParams.get("return") || "/edu/admin";

  const navItems = [
    { id: "profile", label: "Profile", icon: UserCircle, to: "/profile", active: true },
    { id: "admin", label: "EDU Admin", icon: Monitor, to: "/edu/admin" },
    { id: "mail", label: "Oikos Mail", icon: Mail, to: "/mail" },
    { id: "workspace", label: "Workspace", icon: Workflow, to: "/workspace/admin" },
    { id: "home", label: "Home", icon: Home, to: "/home" },
  ];

  return (
    <main style={styles.page}>
      <aside style={styles.sidebar}>
        <div style={styles.brandBlock}>
          <img src={devicesLogo} alt="Oikos" style={styles.logo} />
          <button type="button" style={styles.profileTile} onClick={() => navigate("/profile")}>
            {profileImageUrl ? (
              <img src={profileImageUrl} alt="" style={styles.profileImage} />
            ) : (
              <span style={styles.profileFallback}>{getInitials(displayName, displayEmail)}</span>
            )}
            <span style={styles.actorCopy}>
              <span style={styles.actorLine}>{displayName}</span>
              <small style={styles.actorRole}>{displayEmail || "Oikos user"}</small>
            </span>
          </button>
        </div>

        <nav style={styles.nav}>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                style={{ ...styles.navButton, ...(item.active ? styles.navButtonActive : {}) }}
                onClick={() => navigate(item.to)}
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <button type="button" style={styles.backButton} onClick={() => navigate(returnPath)}>
          <ArrowLeft size={17} />
          Back
        </button>
      </aside>

      <section style={styles.content}>
        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>Account</div>
            <h1 style={styles.title}>User Profile</h1>
            <p style={styles.subtitle}>
              Manage your photo, contact details, email address, phone number, password, and personal profile.
            </p>
          </div>
        </div>
        <ProfileSettingsPanel />
      </section>
    </main>
  );
}

const styles = {
  page: {
    "--profile": "#D3412A",
    background: "linear-gradient(135deg, #fff7ed 0%, #f7f5ff 45%, #ecfeff 100%)",
    boxSizing: "border-box",
    color: "#17142a",
    display: "grid",
    gap: 18,
    gridTemplateColumns: "280px minmax(0, 1fr)",
    minHeight: "100dvh",
    padding: 18,
  },
  sidebar: {
    background: "rgba(255,255,255,0.72)",
    backdropFilter: "blur(18px) saturate(1.18)",
    WebkitBackdropFilter: "blur(18px) saturate(1.18)",
    border: "1px solid rgba(255,255,255,0.62)",
    borderRadius: 24,
    boxShadow: "0 18px 46px rgba(69,55,129,0.12)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    padding: 16,
  },
  brandBlock: { display: "grid", gap: 8, justifyItems: "center" },
  logo: { height: 72, maxWidth: "100%", objectFit: "contain", objectPosition: "center" },
  profileTile: {
    alignItems: "center",
    background: "rgba(255,255,255,0.76)",
    border: "1px solid rgba(211,65,42,0.22)",
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(69,55,129,0.08)",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    gap: 10,
    minWidth: 0,
    padding: 9,
    textAlign: "left",
    width: "100%",
  },
  profileImage: {
    border: "2px solid rgba(211,65,42,0.24)",
    borderRadius: "50%",
    flex: "0 0 40px",
    height: 40,
    objectFit: "cover",
    width: 40,
  },
  profileFallback: {
    alignItems: "center",
    background: "linear-gradient(135deg, var(--profile), #14b8a6)",
    border: "2px solid rgba(255,255,255,0.78)",
    borderRadius: "50%",
    color: "#ffffff",
    display: "inline-flex",
    flex: "0 0 40px",
    fontSize: 13,
    fontWeight: 950,
    height: 40,
    justifyContent: "center",
    width: 40,
  },
  actorCopy: { display: "grid", gap: 2, minWidth: 0, textAlign: "left" },
  actorLine: {
    color: "#312b4f",
    fontSize: 13,
    fontWeight: 950,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actorRole: {
    color: "#7a718e",
    fontSize: 11,
    fontWeight: 900,
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  nav: { display: "grid", gap: 8 },
  navButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(211,65,42,0.20)",
    borderRadius: 18,
    color: "#4f4670",
    cursor: "pointer",
    display: "flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 10,
    minHeight: 46,
    padding: "0 14px",
    textAlign: "left",
  },
  navButtonActive: {
    background: "rgba(211,65,42,0.16)",
    borderColor: "rgba(211,65,42,0.32)",
    color: "#9f2f20",
  },
  backButton: {
    alignItems: "center",
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(148,163,184,0.22)",
    borderRadius: 18,
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: 14,
    fontWeight: 900,
    gap: 9,
    justifyContent: "center",
    marginTop: "auto",
    minHeight: 44,
  },
  content: {
    display: "grid",
    gap: 16,
    minWidth: 0,
  },
  header: {
    background: "rgba(255,255,255,0.64)",
    backdropFilter: "blur(18px) saturate(1.14)",
    WebkitBackdropFilter: "blur(18px) saturate(1.14)",
    border: "1px solid rgba(255,255,255,0.62)",
    borderRadius: 24,
    boxShadow: "0 18px 46px rgba(69,55,129,0.10)",
    padding: 22,
  },
  eyebrow: {
    color: "var(--profile)",
    fontSize: 12,
    fontWeight: 950,
    textTransform: "uppercase",
  },
  title: {
    color: "#17142a",
    fontSize: 34,
    lineHeight: 1.1,
    margin: "4px 0 8px",
  },
  subtitle: {
    color: "#64748b",
    fontSize: 14,
    fontWeight: 750,
    lineHeight: 1.45,
    margin: 0,
    maxWidth: 760,
  },
};
